import { GridCol, GridRow, Select } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapDistributorSpot } from '@websrc/DataMappers/mapSpot'
import { SpotDistributorCard } from '@websrc/Spot/Distributor/Card/SpotDistributorCard'
import { CSLocales } from '@websrc/Typings/ContentStack'
import React, { ChangeEvent, useContext, useMemo, useState } from 'react'

import * as SC from './SpotDistributor.style'

export interface SpotDistributorProps {
  spot: ReturnType<typeof mapDistributorSpot>['distributor']
  imageLazy?: boolean
}

const marketMap: {
  [key: string]: CSLocales
} = {
  Australia: 'en-au',
  Brazil: 'pt-br',
  Canada: 'fr-ca',
  China: 'zh-cn',
  Denmark: 'da-dk',
  Finland: 'fi-fi',
  France: 'fr-fr',
  Germany: 'de-de',
  Hungary: 'hu-hu',
  Japan: 'ja-jp',
  Korea: 'ko-kr',
  Mexico: 'es-mx',
  Netherlands: 'nl-nl',
  Norway: 'nb-no',
  Poland: 'pl-pl',
  Portugal: 'pt-pt',
  Russia: 'ru-ru',
  Spain: 'es-es',
  Sweden: 'sv-se',
  'United Kingdom': 'en-gb',
  Vietnam: 'vi-vn',
}

export function SpotDistributor({ spot, imageLazy }: SpotDistributorProps) {
  const { headline, description, markets, authorizedLogo, authorizedText, nonAuthorizedText } = spot
  const locale = useLocale()
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const initialMarket = useMemo(
    () => markets.find((market) => marketMap[market.name] === locale) ?? null,
    [locale, markets]
  )

  const [market, setMarket] = useState<(typeof markets)[0] | null>(initialMarket)
  const t = useStaticText()

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const [selected] = markets.filter((mark) => mark.name === e.target.value)

    matomo.trackEvent(
      'engagement',
      matomoPageTemplate,
      [spot.headline],
      [`select country ${selected.name}`],
      'drop-down list'
    )

    setMarket(selected)
  }

  return (
    <SC.Wrapper>
      <GridRow>
        <SC.InfoWrapper desktop={5}>
          <SC.Title>{headline}</SC.Title>
          <SC.Description>{description}</SC.Description>
          <SC.SelectWrapper>
            <Select aria-label={t.shop.select_country} onChange={handleSelect} value={market?.name}>
              <option>{t.shop.select_country}</option>
              {markets.map(({ name }, i) => (
                <option key={i} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </SC.SelectWrapper>
        </SC.InfoWrapper>
        <SC.AssetWrapper desktop={7}>
          <SC.AssetContent
            lazyLoading={imageLazy}
            sizes={`(min-width: 1024px) 700px`}
            srcSetWidths={[320, 400, 600, 800, 1200]}
            {...spot.asset}
          />
        </SC.AssetWrapper>
      </GridRow>
      {market && (
        <GridRow>
          <GridCol>
            {market.isAuth ? (
              <Markdown markdown={authorizedText} />
            ) : (
              <Markdown markdown={nonAuthorizedText} />
            )}
          </GridCol>
        </GridRow>
      )}
      {market && (
        <SC.DistributorsWrapper>
          {market.distributors.map((dist, i) => (
            <SpotDistributorCard
              authorizedAsset={market.isAuth ? authorizedLogo : null}
              distributor={dist}
              isMainMarket={market?.isMain}
              key={i}
            />
          ))}
        </SC.DistributorsWrapper>
      )}
    </SC.Wrapper>
  )
}
